import { useContext, useEffect, useMemo, useState } from "react";
import {
  MediaItem,
  PendingEndlink,
  SendCommunicationAttachmentResponse
} from "@coxauto-ui/cxm/communications/interfaces";
import { useCxmFlags } from "@coxauto-ui/core/third-party/launch-darkly";
import { ConversationContext } from "../context";
import {
  ITextReplyContext,
  SelectedDealerPhoneInformation,
  defaultDealerPhoneValidation,
  defaultSelectedDealerPhoneInformation
} from "../context/text-reply-context";

export function useTextReplyState(): ITextReplyContext {
  const { dealerCommunicationDetails } = useContext(ConversationContext);

  const [replyText, setReplyText] = useState("");
  const [attachments, setAttachments] = useState<
    SendCommunicationAttachmentResponse[]
  >([]);
  const [selectedDealerPhoneInformation, setSelectedDealerPhoneInformation] =
    useState<SelectedDealerPhoneInformation>(
      defaultSelectedDealerPhoneInformation
    );
  const [selectedToChannelValue, setSelectedToChannelValue] = useState("");
  const [selectedTemplateLoadState, setSelectedTemplateLoadState] = useState({
    hasLoaded: false,
    loadedTemplateId: null as string | null
  });
  const [isTextOptInPending, setIsTextOptInPending] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState<MediaItem[]>([]);
  const [pendingEndlinks, setPendingEndlinks] = useState<PendingEndlink[]>([]);

  // Extract primitive to keep useEffect dep array stable —
  // using the whole object would cause an infinite loop since the effect
  // itself calls setSelectedDealerPhoneInformation.
  const selectedPhoneNumber = selectedDealerPhoneInformation.phoneNumber;

  // Flag to enable phone validation checks that can block texting if not met
  const {
    "nx.cxm.dealer-phone-status-checks": dealerPhoneStatusChecksEnabled
  } = useCxmFlags();
  const isPhoneValidationBlocking =
    dealerPhoneStatusChecksEnabled &&
    !selectedDealerPhoneInformation.validation.canProceed;

  useEffect(() => {
    if (!selectedPhoneNumber || !dealerCommunicationDetails) {
      setSelectedDealerPhoneInformation(prev => ({
        ...prev,
        validation: defaultDealerPhoneValidation
      }));
      return;
    }

    const { dealerPhoneNumber, userPhoneNumbers } = dealerCommunicationDetails;

    const phoneEntry =
      dealerPhoneNumber.phoneNumber === selectedPhoneNumber
        ? dealerPhoneNumber
        : userPhoneNumbers.find(p => p.phoneNumber === selectedPhoneNumber);

    if (!phoneEntry) {
      setSelectedDealerPhoneInformation(prev => ({
        ...prev,
        validation: defaultDealerPhoneValidation
      }));
      return;
    }

    setSelectedDealerPhoneInformation(prev => ({
      ...prev,
      validation: {
        // ?? false: defend against API responses that are missing these fields
        // despite the TypeScript contract declaring them required
        canProceed: phoneEntry.canProceed ?? false,
        campaignApproved: phoneEntry.campaignApproved ?? false,
        isPhoneNumberReady: phoneEntry.isPhoneNumberReady ?? false
      }
    }));
  }, [selectedPhoneNumber, dealerCommunicationDetails]);

  return useMemo(
    () => ({
      replyText,
      attachments,
      selectedDealerPhoneInformation,
      selectedToChannelValue,
      selectedTemplateLoadState,
      isTextOptInPending,
      isPhoneValidationBlocking,
      selectedMedia,
      pendingEndlinks,
      setReplyText,
      setAttachments,
      setSelectedDealerPhoneInformation,
      setSelectedToChannelValue,
      setSelectedTemplateLoadState,
      setIsTextOptInPending,
      setSelectedMedia,
      setPendingEndlinks
    }),
    [
      replyText,
      attachments,
      selectedDealerPhoneInformation,
      selectedToChannelValue,
      selectedTemplateLoadState,
      isTextOptInPending,
      isPhoneValidationBlocking,
      selectedMedia,
      pendingEndlinks
    ]
  );
}
